import { VoidCallback, VoidCallback1 } from '@deqo/foundation';
import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { LFC } from '../../../Legacy';
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { UserLiteData } from '../../../api/ApiTypes';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Icon } from '../../../concepts/resources/Icon';
import { useLoader } from '../../../platform/Hook';
import { TypeaheadInput } from '../../../ui';
import { DropButton } from '../../../ui/DropButton';
import { Pill } from '../../../ui/Pill';
import { HelpTip } from '../../../ui/Tip';
import { useResolve } from '../../Container';
import { Counter } from '../../common/Users';
import { labelSorter } from '../../dashboard/Models';
import { Copy, Flexer, Title } from '../../dashboard/elements/LayoutElements';
import { ParticipantManager } from '../participants/ParticipantManager';
import { meetingSetHosts } from './MeetingCommands';
import { useActiveMeeting } from './MeetingHooks';

const facilitatorHelp =
    'Hosts have access to extra controls that allow them to manage the meeting.  ' +
    'By default, the creator of the meeting will be set as the host.  You can add' +
    'or remove more using the controls below.  If you prefer, you can opt for no ' +
    'specified host, which will grant host powers to everyone in the board.';

const UserSelector: LFC<{ onSelect: VoidCallback1<UserLiteData> }> = ({ onSelect }) => {
    const api = useResolve(ApiClient);
    const userSource = async (query: string) => {
        const users = await api.exec(v2.users.list({ like: query, count: 20 }));
        return users.map(x => ({ label: x.name, ...x })).sort(labelSorter);
    };
    return <TypeaheadInput
        placeholder="Search..."
        typeaheadSource={userSource}
        onTypeaheadSelect={x => {
            onSelect(x as unknown as UserLiteData);
            return true;
        }}
    />;
};

const UserName = styled.div`
    font-weight: 450;
`;

const UserEmail = styled.div`
    margin-top: -.125rem;
    font-size: .875rem;
    opacity: 0.8;
`;

const UserPill = React.memo<{ user: Partial<UserLiteData>, onClose: VoidCallback }>(({ user, onClose }) => {

    const api = useResolve(ApiClient);
    const ptpts = useResolve(ParticipantManager);

    const [loading, data] = useLoader(async () => {
        if (user.name) return user;
        if (ptpts.get(user.id)) return ptpts.get(user.id);
        const results = await api.exec(v2.users.list({ in: [user.id] }));
        return results[0] ?? user;
    }, [user]);


    return <Pill>
        <Pill.Inner>
            <Flexer flex="row" center gap=".5">
                <Counter size={1.5} unit="rem" color={data?.color} photo={data?.photo}>SW</Counter>
                <Flexer flex="column">
                    <UserName>{loading ? 'Loading...' : data?.name ?? 'Unknown User'}</UserName>
                    <UserEmail>{data?.email}</UserEmail>
                </Flexer>
            </Flexer>
        </Pill.Inner>
        <Pill.Close onClick={onClose} />
    </Pill>;
});

export const ManageMeetingHostsView: LFC<{ onAction: VoidCallback }> = ({ onAction }) => {

    const dispatch = useResolve(CommandDispatcher);
    const meeting = useActiveMeeting();

    const initial = useMemo(() => meeting.facilitators.map(id => ({ id })), [meeting.facilitators]);
    const [hosts, setHosts] = useState(initial as Partial<UserLiteData>[]);

    const onAdd = (user: UserLiteData) => setHosts(x => x.some(u => u.id === user.id) ? x : [...x, user]);
    const onRemove = (userId: string) => setHosts(x => x.filter(e => e.id !== userId));
    const onApply = useCallback(() => {
        dispatch(meetingSetHosts, hosts.map(x => x.id));
        onAction();
    }, [hosts, onAction]);

    return <Flexer inset padding="1" flex="column" justify="space-between">
        <Flexer>
            <Copy>
                Add or remove hosts for this meeting: <HelpTip content={facilitatorHelp} />
            </Copy>
            <UserSelector onSelect={onAdd} />
            <Flexer margin="1 -.25">
                {hosts.map(x => <UserPill key={x.id} user={x} onClose={() => onRemove(x.id)} />)}
            </Flexer>
        </Flexer>
        <Flexer flex="row" gap=".5" center padding=".5 0">
            <DropButton padding=".25 .75" overflow=".25" onClick={onAction}>
                <Icon src="deqo://icons/hero/arrow-sm-left" size="1.5" />
                <Title weight="450" flush>Go Back</Title>
            </DropButton>
            <DropButton padding=".25 .75" overflow=".25" onClick={onApply}>
                <Icon src="deqo://icons/hero/check" size="1.5" />
                <Title weight="450" flush>Apply Changes</Title>
            </DropButton>
        </Flexer>
    </Flexer>;
};
